import {
  Flex,
  Heading,
  Table,
  TableContainer,
  Tbody,
  Thead,
  Td,
  Th,
  Tr,
} from '@chakra-ui/react';
import React from 'react';

import { date as commonDate, finleyAssert } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { Loans } from './Loans';
import {
  entityBoxStyle,
  entityNameHeadingProps,
  entitySubPropertyHeadingProps,
} from './shared';
import { Icon } from '~/packages/common-frontend/components/icons';
import { InfoCard } from '~/packages/common-frontend/components/InfoCard';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  dollarNumberWithCommas,
  formatCurrencyAmount,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';

type CommitmentsProps = {
  commitments: NonNullable<
    CreditAgreement.DomainEntity.CreditAgreementFullArtifact['loanMechanicsV1']
  >['commitments'];
  loans: NonNullable<
    CreditAgreement.DomainEntity.CreditAgreementFullArtifact['loanMechanicsV1']
  >['loans'];
  pricingOptions: NonNullable<
    CreditAgreement.DomainEntity.CreditAgreementFullArtifact['loanMechanicsV1']
  >['pricingOptions'];
};

export const Commitments = ({
  commitments,
  loans,
  pricingOptions,
}: CommitmentsProps) => {
  if (!commitments) {
    return null;
  }

  return (
    <>
      {commitments.map(
        ({ key, name, maturityDate, limit, loans: commitmentLoans }) => {
          const loansForCommitment = commitmentLoans.map(
            ({ loanKey: commitmentLoanKey }) => {
              const loan = loans.find(
                ({ key: loanKey }) => commitmentLoanKey === loanKey,
              );
              finleyAssert(
                loan,
                `Expected loan for key [${commitmentLoanKey}]`,
              );
              return loan;
            },
          );

          return (
            <Flex style={entityBoxStyle} key={key}>
              <Heading {...entityNameHeadingProps}>{name}</Heading>
              <Flex w="100%" gap="2em">
                <InfoCard
                  title="Commitment Limit"
                  value={formatCurrencyAmount(limit.amount, limit.currency)}
                  icon={<Icon icon="dollar-sign" size="24px" />}
                  w="20rem"
                />
                <InfoCard
                  title="Maturity Date"
                  value={commonDate.fullDateStrToHumanDateString(maturityDate)}
                  icon={<Icon icon="calendar-plus" size="24px" />}
                  w="20rem"
                />
                {limit.sublimit && <Typography>{limit.sublimit}</Typography>}
              </Flex>
              <Heading {...entitySubPropertyHeadingProps}>Lenders</Heading>
              <TableContainer w="100%">
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Lender</Th>
                      <Th>Amount</Th>
                      <Th>Pro Rata %</Th>
                      <Th>Servicer Fee %</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {commitmentLoans[0].lenders.map(
                      ({
                        entity: { id, name: lenderName },
                        amount,
                        servicerFee,
                      }) => (
                        <Tr key={id}>
                          <Td>{lenderName}</Td>
                          {/* @jendevelops - might need to pipe in currency eventually */}
                          <Td>{dollarNumberWithCommas(amount)}</Td>
                          <Td>
                            {percentageToPercentageLabel(
                              (amount / limit.amount) * 100,
                              6,
                            )}
                          </Td>
                          <Td>
                            {percentageToPercentageLabel(
                              (servicerFee?.rate ?? 0) * 100,
                              6,
                            )}
                          </Td>
                        </Tr>
                      ),
                    )}
                  </Tbody>
                </Table>
              </TableContainer>
              <Heading {...entitySubPropertyHeadingProps}>Products</Heading>
              <Loans
                loans={loansForCommitment}
                pricingOptions={pricingOptions}
              />
            </Flex>
          );
        },
      )}
    </>
  );
};
